import { computed, ref } from "vue";
import { EntityStore } from "@/stores/factory/entity-store-factory";
import { PageResponse } from "@/types/api-types";

interface ListState<T> {
  list: T[];
  changed: boolean;
  init: boolean;
}

interface CursorPagerStoreFactoryParams<V, OptionalParams = {}> {
  entityStore: EntityStore<V>;
  fetch: (cursor?: string, options?: OptionalParams) => Promise<V[]>;
  cursorEncoder: (entity: V, pointsToNextItems: boolean) => string;
}

interface PageStoreFactoryParams<V, OptionalParams = {}> {
  entityStore: EntityStore<V>;
  fetch: (page: number, options?: OptionalParams) => Promise<PageResponse<V>>;
}

export const cursorEncoderById = (
  entity: Entity,
  pointsToNextItems: boolean
) => {
  return btoa(
    JSON.stringify({
      id: entity.id,
      _pointsToNextItems: pointsToNextItems,
    })
  );
};

export const cursorPagerStoreFactory = <V, OptionalParams>({
  entityStore,
  fetch,
  cursorEncoder,
}: CursorPagerStoreFactoryParams<V, OptionalParams>) => {
  const state = ref<ListState<string | number>>({
    list: [],
    changed: false,
    init: false,
  });

  const dataList = computed<V[]>({
    get: () => {
      return state.value.list
        .map((id) => entityStore.getEntity(id as string))
        .filter((item) => item !== undefined) as V[];
    },
    set: (list: V[]) => {
      state.value.list = list.map((item) => entityStore.getKey(item));
    },
  });

  async function initListIfAbsent(
    force: boolean = false,
    options?: OptionalParams
  ): Promise<boolean> {
    const shouldInit =
      force || (state.value.list.length === 0 && !state.value.init);
    if (shouldInit) {
      const list = await fetch(undefined, options);
      state.value.list = list.map((item) => entityStore.getKey(item));
      state.value.init = true;
      state.value.changed = false;
    }
    return shouldInit;
  }

  async function fetchNewerList(options?: OptionalParams): Promise<void> {
    const currentList = state.value.list;
    if (currentList.length === 0) {
      await initListIfAbsent(true, options);
    } else {
      const firstIdx = currentList[0] as string;
      const firstItem = entityStore.getEntity(firstIdx);
      const cursor = cursorEncoder(firstItem!, false);
      const list = await fetch(cursor, options);
      currentList.unshift(...list.map((item) => entityStore.getKey(item)));
    }
  }

  async function fetchOlderList(options?: OptionalParams): Promise<boolean> {
    const currentList = state.value.list;
    if (currentList.length === 0) {
      await initListIfAbsent(true, options);
      return state.value.list.length !== 0;
    } else {
      const lastIdx = currentList[currentList.length - 1] as string;
      const lastItem = entityStore.getEntity(lastIdx);
      const cursor = cursorEncoder(lastItem!, true);
      const list = await fetch(cursor, options);
      currentList.push(...list.map((item) => entityStore.getKey(item)));
      return list.length !== 0;
    }
  }

  async function fetchOlderListWithCount(options?: OptionalParams): Promise<{
    hasMore: boolean;
    count: number;
  }> {
    const currentList = state.value.list;
    if (currentList.length === 0) {
      await initListIfAbsent(true);
      return {
        hasMore: state.value.list.length !== 0,
        count: state.value.list.length,
      };
    } else {
      const lastIdx = currentList[currentList.length - 1] as string;
      const lastItem = entityStore.getEntity(lastIdx);
      const cursor = cursorEncoder(lastItem!, true);
      const list = await fetch(cursor, options);
      currentList.push(...list.map((item) => entityStore.getKey(item)));
      return { hasMore: list.length !== 0, count: list.length };
    }
  }

  function resetState() {
    state.value = {
      list: [],
      changed: false,
      init: false,
    };
  }

  return {
    state,
    dataList,
    fetchNewerList,
    fetchOlderList,
    fetchOlderListWithCount,
    initListIfAbsent,
    resetState,
  };
};

export const pageStoreFactory = <V, OptionalParams>({
  entityStore,
  fetch,
}: PageStoreFactoryParams<V, OptionalParams>) => {
  const state = ref<
    ListState<string | number> & { page: number; total: number }
  >({
    list: [],
    changed: false,
    init: false,
    page: 1,
    total: 0,
  });

  const dataList = computed<V[]>({
    get: () => {
      return state.value.list
        .map((id) => entityStore.getEntity(id as string))
        .filter((item) => item !== undefined) as V[];
    },
    set: (list: V[]) => {
      state.value.list = list.map((item) => entityStore.getKey(item));
    },
  });

  async function initListIfAbsent(
    force: boolean = false,
    options?: OptionalParams
  ): Promise<boolean> {
    const shouldInit =
      force || (state.value.list.length === 0 && !state.value.init);
    if (shouldInit) {
      const res = await fetch(1, options);
      const { data: list, total } = res.page;
      state.value.list = list.map((item) => entityStore.getKey(item));
      state.value.init = true;
      state.value.changed = false;
      state.value.total = total ?? 0;
      state.value.page = 1;
    }
    return shouldInit;
  }

  async function fetchNextPage(options?: OptionalParams): Promise<boolean> {
    const currentList = state.value.list;
    if (currentList.length === 0) {
      await initListIfAbsent(true, options);
      return state.value.list.length !== 0;
    } else {
      const nextPage = state.value.page + 1;
      const res = await fetch(nextPage, options);
      const { data: list, total } = res.page;
      currentList.push(...list.map((item) => entityStore.getKey(item)));
      state.value.page = nextPage;
      state.value.total = total ?? 0;
      return list.length !== 0;
    }
  }

  async function fetchPreviousPage(options?: OptionalParams): Promise<void> {
    const currentList = state.value.list;
    if (currentList.length === 0) {
      await initListIfAbsent(true, options);
    } else {
      const previousPage = state.value.page - 1;
      const res = await fetch(previousPage, options);
      const { data: list, total } = res.page;
      currentList.unshift(...list.map((item) => entityStore.getKey(item)));
      state.value.page = previousPage;
      state.value.total = total ?? 0;
    }
  }

  function resetState() {
    state.value = {
      list: [],
      changed: false,
      init: false,
      page: 1,
      total: 0,
    };
  }

  return {
    state,
    dataList,
    fetchNextPage,
    fetchPreviousPage,
    initListIfAbsent,
    resetState,
  };
};
